import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Link, MenuItem, Select, Stack, Tab, Table,
  TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import { LookupsApi, RunApi } from './api';
import type {
  LookupDefinition, LookupDType, LookupRangeType, LookupRow, Process,
  RequiredLookupTable,
} from './types';

interface Props {
  process: Process;
  params: Record<string, unknown>;
  onChange: (lookups: LookupDefinition[]) => void;
}

const DTYPES: { value: LookupDType; label: string }[] = [
  { value: 'N', label: 'Number' },
  { value: 'D', label: 'Date' },
  { value: 'S', label: 'String' },
];

function emptyDef(name: string): LookupDefinition {
  return { lookup_table: name.toUpperCase(), lookup_type: 'S', key_data_type: 'N', result_data_type: 'N', rows: [] };
}

// Canonical, comparable signature for a single lookup row (trims/normalises
// nulls vs empty strings so cosmetic differences don't read as a mismatch).
function rowKey(r: LookupRow): string {
  return JSON.stringify([
    String(r.result_value ?? '').trim(),
    String(r.min_key_value ?? '').trim(),
    r.max_key_value == null ? '' : String(r.max_key_value).trim(),
    r.eff_from ?? '',
    r.valid_upto ?? '',
  ]);
}

type RowStatus = 'same' | 'db-only' | 'json-only';

interface SyncDiff {
  metaDiff: boolean;
  inSync: boolean;
  rows: { row: LookupRow; status: RowStatus }[];
}

// Diff the JSON (embedded/working) definition against the DB copy. Rows present
// on both sides are 'same'; rows only in the DB or only in JSON are flagged so
// the dialog can highlight exactly what differs and which way to sync.
function diffDefs(jsonDef: LookupDefinition | undefined, dbDef: LookupDefinition): SyncDiff {
  const dbKeys = dbDef.rows.map(rowKey);
  const jsonKeys = (jsonDef?.rows ?? []).map(rowKey);
  const jsonSet = new Set(jsonKeys);
  const dbSet = new Set(dbKeys);
  const rows: { row: LookupRow; status: RowStatus }[] = [];
  dbDef.rows.forEach((r, i) => rows.push({ row: r, status: jsonSet.has(dbKeys[i]) ? 'same' : 'db-only' }));
  (jsonDef?.rows ?? []).forEach((r, i) => {
    if (!dbSet.has(jsonKeys[i])) rows.push({ row: r, status: 'json-only' });
  });
  const metaDiff = !!jsonDef && (
    jsonDef.lookup_type !== dbDef.lookup_type
    || jsonDef.key_data_type !== dbDef.key_data_type
    || jsonDef.result_data_type !== dbDef.result_data_type
  );
  const inSync = !!jsonDef && !metaDiff && rows.every((x) => x.status === 'same');
  return { metaDiff, inSync, rows };
}

export default function LookupEditor({ process, params, onChange }: Props) {
  // Working copy of lookup definitions, keyed by UPPER-CASE lookup table name.
  const [defs, setDefs] = useState<Record<string, LookupDefinition>>({});
  const [tables, setTables] = useState<RequiredLookupTable[]>([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seeded = useRef(false);

  // "View saved data in database" dialog — shows the GENLOOKUP_TABLE copy of
  // a lookup as currently persisted in the target DB (independent of the
  // embedded/working definition above).
  const [dbView, setDbView] = useState<{ name: string; def: LookupDefinition | null } | null>(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState<string | null>(null);
  const [dbNotFound, setDbNotFound] = useState(false);
  const [syncing, setSyncing] = useState<null | 'json2db' | 'db2json'>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const viewInDb = useCallback(async (name: string) => {
    setDbView({ name, def: null });
    setDbError(null);
    setDbNotFound(false);
    setSyncMsg(null);
    setDbLoading(true);
    try {
      setDbView({ name, def: await LookupsApi.get(name) });
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes('404') || /not found/i.test(msg)) setDbNotFound(true);
      else setDbError(msg);
    } finally {
      setDbLoading(false);
    }
  }, []);

  // Push the JSON (embedded/working) definition into the target DB, overwriting
  // the GENLOOKUP_TABLE copy. Refreshes the DB view on success.
  const syncJsonToDb = useCallback(async (name: string, jsonDef: LookupDefinition) => {
    if (!confirm(
      `Overwrite the DATABASE copy of "${name}" with the JSON definition `
      + `(${jsonDef.rows.length} row(s))? This rewrites GENLOOKUP_TABLE.`,
    )) return;
    setSyncing('json2db');
    setDbError(null);
    try {
      await LookupsApi.save({ ...jsonDef, lookup_table: name });
      await viewInDb(name);
      setSyncMsg('Database updated from JSON.');
    } catch (e) {
      setDbError((e as Error).message);
    } finally {
      setSyncing(null);
    }
  }, [viewInDb]);

  // Pull the DB copy into the working/embedded definition. Persisted to the
  // process JSON only when the user Saves the process.
  const syncDbToJson = useCallback((name: string, dbDef: LookupDefinition) => {
    if (!confirm(
      `Replace the JSON definition of "${name}" with the DATABASE copy `
      + `(${dbDef.rows.length} row(s))? Save the process afterwards to keep it.`,
    )) return;
    setSyncing('db2json');
    // commit() lives below; defer the actual write to the click handler via a
    // window microtask isn't needed — commitRef is wired after declaration.
    commitRef.current?.({ name, def: { ...dbDef, lookup_table: name } });
    setSyncMsg('JSON updated from database. Save the process to persist.');
    setSyncing(null);
  }, []);

  // Bridges syncDbToJson (declared before commit) to the commit() below.
  const commitRef = useRef<((p: { name: string; def: LookupDefinition }) => void) | null>(null);

  // Seed the working copy from the process's embedded lookups (once).
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const seed: Record<string, LookupDefinition> = {};
    for (const d of process.lookups || []) {
      if (d?.lookup_table) seed[d.lookup_table.toUpperCase()] = d;
    }
    setDefs(seed);
  }, [process.lookups]);

  const commit = useCallback(
    (next: Record<string, LookupDefinition>) => {
      setDefs(next);
      onChange(Object.values(next));
    },
    [onChange],
  );

  // Wire the ref used by syncDbToJson (declared above commit) so it can write a
  // single lookup into the working copy through the normal commit() path.
  commitRef.current = ({ name, def }) => commit({ ...defs, [name.toUpperCase()]: def });

  const detect = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Send the live process body (unsaved edits included) + current params so
      // dynamic lookups resolve their distinct values from the real data.
      const resp = await RunApi.lookupsRequired({ process, params });
      setTables(resp.tables);
      setDefs((prev) => {
        const next = { ...prev };
        for (const t of resp.tables) {
          // Don't clobber edits the user already made in this session.
          if (!next[t.lookup_table]) next[t.lookup_table] = t.definition || emptyDef(t.lookup_table);
        }
        onChange(Object.values(next));
        return next;
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [process, params, onChange]);

  // Auto-detect once on first mount so tabs appear without a manual click.
  useEffect(() => {
    void detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab order: detected tables first, then any embedded-only lookups.
  const tabNames = useMemo(() => {
    const names: string[] = tables.map((t) => t.lookup_table);
    for (const k of Object.keys(defs)) if (!names.includes(k)) names.push(k);
    return names;
  }, [tables, defs]);

  const tableInfo = (name: string) => tables.find((t) => t.lookup_table === name);

  const activeName = tabNames[Math.min(active, Math.max(0, tabNames.length - 1))];
  const def = activeName ? defs[activeName] || emptyDef(activeName) : null;

  const setDef = (name: string, patch: Partial<LookupDefinition>) =>
    commit({ ...defs, [name]: { ...(defs[name] || emptyDef(name)), ...patch } });

  const setRow = (name: string, idx: number, patch: Partial<LookupRow>) => {
    const d = defs[name] || emptyDef(name);
    const rows = d.rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setDef(name, { rows });
  };
  const addRow = (name: string) => {
    const d = defs[name] || emptyDef(name);
    setDef(name, { rows: [...d.rows, { result_value: '', min_key_value: '', max_key_value: '' }] });
  };
  const delRow = (name: string, idx: number) => {
    const d = defs[name] || emptyDef(name);
    setDef(name, { rows: d.rows.filter((_, i) => i !== idx) });
  };

  const hasLookups = tabNames.length > 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Lookups
        </Typography>
        <Tooltip title="Re-detect lookup tables from the current SQL + data (expands dynamic lookups into one tab per distinct value)">
          <span>
            <Button size="small" startIcon={<RefreshIcon />} onClick={detect} disabled={loading}>
              {loading ? 'Detecting…' : 'Detect from data'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Lookups are stored in the process and synced to the target database. A lookup whose name is a
        column (e.g. <code>region, total_sales</code>) is <strong>dynamic</strong>: one tab is created
        for each distinct value found in the data.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {!hasLookups ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          No lookup (L) calculations detected. Add an <code>L</code> calculation, then click
          “Detect from data”.
        </Typography>
      ) : (
        <>
          <Tabs
            value={Math.min(active, tabNames.length - 1)}
            onChange={(_, v) => setActive(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
          >
            {tabNames.map((name) => {
              const info = tableInfo(name);
              const dyn = info?.source === 'dynamic';
              return (
                <Tab
                  key={name}
                  sx={{ minHeight: 36, py: 0.5, textTransform: 'none' }}
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{name}</span>
                      {dyn && <Chip label="dyn" size="small" color="secondary" sx={{ height: 16, fontSize: 13 }} />}
                    </Stack>
                  }
                />
              );
            })}
          </Tabs>

          {def && activeName && (
            <Box sx={{ pt: 1.5 }}>
              {(() => {
                const info = tableInfo(activeName);
                if (info?.source === 'dynamic') {
                  return (
                    <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                      Dynamic lookup from column <strong>{info.dynamic_column}</strong> = value{' '}
                      <strong>{activeName}</strong>.
                    </Alert>
                  );
                }
                return null;
              })()}

              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                <StorageIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  variant="caption"
                  onClick={() => viewInDb(activeName)}
                >
                  Compare &amp; sync with database (GENLOOKUP_TABLE)
                </Link>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary">Match</Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={def.lookup_type}
                    onChange={(e) => setDef(activeName, { lookup_type: e.target.value as LookupRangeType })}
                    sx={{ minWidth: 130 }}
                  >
                    <MenuItem value="S">Slab (range)</MenuItem>
                    <MenuItem value="F">Fixed (exact)</MenuItem>
                  </Select>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Key type</Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={def.key_data_type}
                    onChange={(e) => setDef(activeName, { key_data_type: e.target.value as LookupDType })}
                    sx={{ minWidth: 110 }}
                  >
                    {DTYPES.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                  </Select>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Result type</Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={def.result_data_type}
                    onChange={(e) => setDef(activeName, { result_data_type: e.target.value as LookupDType })}
                    sx={{ minWidth: 110 }}
                  >
                    {DTYPES.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                  </Select>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <TextField
                  label="Description" size="small" value={def.descr ?? ''}
                  onChange={(e) => setDef(activeName, { descr: e.target.value })}
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  label="Short desc" size="small" value={def.sh_descr ?? ''}
                  onChange={(e) => setDef(activeName, { sh_descr: e.target.value })}
                  sx={{ minWidth: 140 }}
                />
                <TextField
                  label="Linkage (lookup_use)" size="small" value={def.lookup_use ?? ''}
                  onChange={(e) => setDef(activeName, { lookup_use: e.target.value })}
                  disabled={tableInfo(activeName)?.source === 'dynamic'}
                  helperText={
                    tableInfo(activeName)?.source === 'dynamic'
                      ? 'Auto — links this calc’s dynamic lookup tables'
                      : 'Optional grouping tag'
                  }
                  sx={{ minWidth: 220 }}
                />
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Result value</TableCell>
                    <TableCell>{def.lookup_type === 'F' ? 'Key' : 'Min key'}</TableCell>
                    {def.lookup_type === 'S' && <TableCell>Max key</TableCell>}
                    <TableCell>Eff. from</TableCell>
                    <TableCell>Valid upto</TableCell>
                    <TableCell width={40} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {def.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <TextField variant="standard" value={r.result_value}
                          onChange={(e) => setRow(activeName, i, { result_value: e.target.value })} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" value={r.min_key_value}
                          onChange={(e) => setRow(activeName, i, { min_key_value: e.target.value })} />
                      </TableCell>
                      {def.lookup_type === 'S' && (
                        <TableCell>
                          <TextField variant="standard" value={r.max_key_value ?? ''}
                            onChange={(e) => setRow(activeName, i, { max_key_value: e.target.value })} />
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField variant="standard" placeholder="YYYY-MM-DD" value={r.eff_from ?? ''}
                          onChange={(e) => setRow(activeName, i, { eff_from: e.target.value || null })} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" placeholder="YYYY-MM-DD" value={r.valid_upto ?? ''}
                          onChange={(e) => setRow(activeName, i, { valid_upto: e.target.value || null })} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => delRow(activeName, i)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {def.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="caption" color="text.secondary">No rows yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Button size="small" startIcon={<AddIcon />} onClick={() => addRow(activeName)} sx={{ mt: 1 }}>
                Add row
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Saved-in-DB viewer + sync. Compares the GENLOOKUP_TABLE copy in the
          target database against the JSON (embedded/working) definition,
          highlights what differs, and lets the user sync either direction. */}
      {(() => {
        const name = dbView?.name;
        const jsonDef = name ? defs[name] : undefined;
        const dbDef = dbView?.def ?? null;
        const diff = dbDef ? diffDefs(jsonDef, dbDef) : null;
        const inSync = !!diff?.inSync;

        const rowBg = (s: RowStatus) =>
          s === 'db-only' ? 'rgba(255, 152, 0, 0.14)'    // in DB, missing in JSON
          : s === 'json-only' ? 'rgba(33, 150, 243, 0.14)' // in JSON, missing in DB
          : 'transparent';

        return (
          <Dialog open={!!dbView} onClose={() => setDbView(null)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon fontSize="small" />
              <span>Lookup in database — <code>{name}</code></span>
              {dbDef && (
                <Chip
                  size="small"
                  label={inSync ? 'In sync' : 'Out of sync'}
                  color={inSync ? 'success' : 'warning'}
                  sx={{ ml: 'auto' }}
                />
              )}
            </DialogTitle>
            <DialogContent dividers>
              {dbLoading && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Loading from GENLOOKUP_TABLE…</Typography>
                </Stack>
              )}
              {dbError && <Alert severity="error" sx={{ mb: 1 }}>{dbError}</Alert>}
              {syncMsg && <Alert severity="success" sx={{ mb: 1 }}>{syncMsg}</Alert>}

              {/* Not in DB yet — offer to create it from the JSON definition. */}
              {!dbLoading && dbNotFound && (
                <Alert severity="warning">
                  <strong>{name}</strong> is not in the database yet. The JSON definition has{' '}
                  {jsonDef?.rows.length ?? 0} row(s). Use <em>Sync JSON → DB</em> below to create it.
                </Alert>
              )}

              {!dbLoading && !dbError && dbDef && diff && (
                <>
                  {inSync ? (
                    <Alert severity="success" sx={{ mb: 1 }}>
                      The database copy matches the JSON definition ({dbDef.rows.length} row(s)).
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Out of sync.
                      {diff.metaDiff && ' Table type/key/result settings differ.'}
                      {' '}
                      {diff.rows.filter((r) => r.status === 'db-only').length} row(s) only in DB,{' '}
                      {diff.rows.filter((r) => r.status === 'json-only').length} row(s) only in JSON.
                      Choose a direction to sync below.
                    </Alert>
                  )}

                  {diff.metaDiff && jsonDef && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      JSON: {jsonDef.lookup_type}/{jsonDef.key_data_type}/{jsonDef.result_data_type}
                      {'  ·  '}
                      DB: {dbDef.lookup_type}/{dbDef.key_data_type}/{dbDef.result_data_type}
                      {'  (type / key / result)'}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: rowBg('db-only') }} />
                      <Typography variant="caption" color="text.secondary">Only in DB</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: rowBg('json-only') }} />
                      <Typography variant="caption" color="text.secondary">Only in JSON</Typography>
                    </Stack>
                  </Stack>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Result value</TableCell>
                        <TableCell>Min / Key</TableCell>
                        <TableCell>Max key</TableCell>
                        <TableCell>Eff. from</TableCell>
                        <TableCell>Valid upto</TableCell>
                        <TableCell>Where</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diff.rows.map(({ row: r, status }, i) => (
                        <TableRow key={i} sx={{ '& td': { bgcolor: rowBg(status) } }}>
                          <TableCell>{r.result_value}</TableCell>
                          <TableCell>{r.min_key_value}</TableCell>
                          <TableCell>{r.max_key_value ?? ''}</TableCell>
                          <TableCell>{r.eff_from ?? ''}</TableCell>
                          <TableCell>{r.valid_upto ?? ''}</TableCell>
                          <TableCell>
                            {status === 'same' ? (
                              <Typography variant="caption" color="text.secondary">both</Typography>
                            ) : (
                              <Chip
                                size="small"
                                label={status === 'db-only' ? 'DB only' : 'JSON only'}
                                color={status === 'db-only' ? 'warning' : 'info'}
                                variant="outlined"
                                sx={{ height: 18, fontSize: 13 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {diff.rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="caption" color="text.secondary">No rows on either side.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Box>
                {name && (jsonDef || dbDef) && (
                  <>
                    <Tooltip title="Overwrite the database (GENLOOKUP_TABLE) with the JSON definition">
                      <span>
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<StorageIcon fontSize="small" />}
                          disabled={!jsonDef || syncing !== null || inSync}
                          onClick={() => jsonDef && syncJsonToDb(name, jsonDef)}
                        >
                          {syncing === 'json2db' ? 'Syncing…' : 'Sync JSON → DB'}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Replace the JSON definition with the database copy (Save the process to keep it)">
                      <span>
                        <Button
                          size="small"
                          color="info"
                          startIcon={<RefreshIcon fontSize="small" />}
                          disabled={!dbDef || syncing !== null || inSync}
                          onClick={() => dbDef && syncDbToJson(name, dbDef)}
                          sx={{ ml: 1 }}
                        >
                          {syncing === 'db2json' ? 'Syncing…' : 'Sync DB → JSON'}
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
              <Button onClick={() => setDbView(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
}
